import React, { useState, useMemo, useEffect } from 'react';
import { Button, Chip, Menu, MenuItem, Stack } from '@mui/material';
import { lighten } from '@mui/system';
import { decodeHtml } from 'components/encode-translation-fix';
import { useLanguage } from '@components/qr-code-menu/use-language';
import Iconify from 'components/iconify';

const LANGUAGE_LABELS = {
  it: 'Italiano',
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
};

const DEFAULT_LANGUAGE = 'en';
const ORIGINAL_LANGUAGE = 'it';

export const HomepageBody = props => {
  const { items, onClickViewMenu, colorTheme, translationsEnabled } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Calcola le lingue disponibili dai menu
  const availableLanguages = useMemo(() => {
    if (!translationsEnabled) return [];

    if (!items?.length)
      return [
        { code: ORIGINAL_LANGUAGE, label: 'Italiano' },
        { code: DEFAULT_LANGUAGE, label: 'English' },
      ];

    // Include sempre italiano e inglese
    const languageSet = new Set([ORIGINAL_LANGUAGE, DEFAULT_LANGUAGE]);

    items.forEach(menu => {
      if (menu.translations) {
        Object.keys(menu.translations).forEach(lang => {
          languageSet.add(lang);
        });
      }
    });

    return Array.from(languageSet)
      .map(code => ({
        code,
        label: LANGUAGE_LABELS[code] || code.toUpperCase(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, translationsEnabled]);

  const { languageCode, setLanguageCode } = useLanguage(translationsEnabled, availableLanguages);

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = newLanguageCode => {
    setAnchorEl(null);
    if (newLanguageCode) {
      setLanguageCode(newLanguageCode);
    }
  };

  const getMenuName = menu => {
    if (languageCode === ORIGINAL_LANGUAGE) return menu.name;

    const translation = menu.translations?.[languageCode]?.translatedName;
    const englishFallback = menu.translations?.[DEFAULT_LANGUAGE]?.translatedName;

    return decodeHtml(translation) ?? decodeHtml(englishFallback) ?? menu.name;
  };

  return (
    <Stack spacing={3} alignItems="center" sx={{ m: 4 }}>
      {translationsEnabled && (
        <>
          <Chip
            icon={<Iconify icon="jam:world" />}
            label={
              availableLanguages.find(lang => lang.code === languageCode)?.label ||
              LANGUAGE_LABELS[DEFAULT_LANGUAGE]
            }
            onClick={handleOpenMenu}
            variant="contained"
            sx={{
              minWidth: '120px',
            }}
          />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => handleCloseMenu(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                minWidth: '150px',
              },
            }}
          >
            {availableLanguages.map(lang => (
              <MenuItem
                key={lang.code}
                onClick={() => handleCloseMenu(lang.code)}
                selected={lang.code === languageCode}
              >
                {lang.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {items?.map(menu => (
        <Button
          key={'menuButton-' + menu.id}
          variant="contained"
          fullWidth
          onClick={() => onClickViewMenu(menu.id)}
          sx={{
            px: 4,
            py: 2,
            borderRadius: 10,
            backgroundColor: colorTheme.main,
            color: colorTheme.buttonText,
            '&:hover': {
              backgroundColor: lighten(colorTheme.main, 0.2),
            },
          }}
        >
          {getMenuName(menu)}
        </Button>
      ))}
    </Stack>
  );
};

export default HomepageBody;
